import React, { useEffect, useState } from "react"
import { MdOutlineFileDownload } from "react-icons/md"
import { NavLink, useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import BreakForm from "../../components/Form/BreakForm"
import Button from "../../components/Form/Button"
import DatePicker from "../../components/Form/DatePicker"
import FormGroup from "../../components/Form/FormGroup"
import Input from "../../components/Form/Input"
import Select from "../../components/Form/Select"
import { useData } from "../../contexts/dataContext"
import { useWebSocketChatContext } from "../../contexts/webSocketChatContext"
import { getUsersBySelectedTeams, getValidUserIdsByTeams } from "../../utils/utils"
import api from "../../services/api"
import Loading from "../layouts/Loading"
import styles from "../ReportDataLead/reportDataLead.module.css"

const readStatusOptions = [
    { id: "read", value: "Lidas" },
    { id: "noRead", value: "Não lidas" },
]

const statusOptions = [
    { id: "waiting", value: "Aguardando" },
    { id: "opened", value: "Em aberto" },
    { id: "paused", value: "Pausadas" },
    { id: "finalized", value: "Finalizadas" },
]

const dateTypeOptions = [
    { id: "last_message", value: "Última mensagem" },
    { id: "conversation_start", value: "Início da conversa" },
]

const ReportDataChat = () => {
    const [filters, setFilters] = useState({ typeDate: "last_message" })
    const [aiAgents, setAiAgents] = useState([])
    const [loading, setLoading] = useState(false)
    const { account_id } = useParams()
    const navigate = useNavigate()
    const { teams, roles, users } = useData()
    const { chatInboxes, chatFlags, getAllChatInboxes, getAllChatFlags } = useWebSocketChatContext()

    useEffect(() => {
        getAllChatInboxes()
        getAllChatFlags()
        api.get("/chat/ai-agents?include_inactive=true").then(response => {
            setAiAgents((response?.data?.data || []).map(agent => ({
                id: agent._id,
                value: `${agent.name || "Agente de IA"}${agent.is_active === false ? " (Inativo)" : ""}`,
            })))
        }).catch(() => setAiAgents([]))
    }, [])

    const inboxOptions = (chatInboxes || []).map(inbox => ({ id: inbox._id, value: inbox.name }))
    const flagOptions = (chatFlags || []).map(flag => ({ id: flag._id, value: flag.name }))

    const handleChange = (name, value) => setFilters(previous => ({
        ...previous,
        [name]: value,
        ...(name === "teams" ? { users: getValidUserIdsByTeams(previous.users, value, users, teams, [], previous.teams) } : {}),
    }))

    const handleChangePeriod = (dateStart, dateEnd, timeStart, timeEnd) => {
        setFilters(previous => ({
            ...previous,
            dateStart: dateStart || "",
            dateEnd: dateEnd || "",
            timeStart: dateStart ? (timeStart || "") : "",
            timeEnd: dateEnd ? (timeEnd || "") : "",
        }))
    }

    const handleSubmit = () => {
        setLoading(true)
        api.post("/reports/chats", filters).then(response => {
            toast.success(response?.data?.message)
            navigate(`/${account_id}/reports/log`)
        }).catch(err => {
            toast.error(err?.response?.data?.message || "Não foi possível solicitar o relatório.")
        }).finally(() => setLoading(false))
    }

    return (
        <div className={styles.content}>
            <div className={styles.card}>
                <div className={styles.header}>
                    <div className={styles.title}>
                        <MdOutlineFileDownload />
                        <div>
                            <strong>Exportar dados do chat</strong>
                            <span>Uma linha por conversa, respeitando seu acesso às caixas de entrada</span>
                        </div>
                    </div>
                    <NavLink to={`/${account_id}/reports/log`}>Histórico de exportações</NavLink>
                </div>

                <div className={styles.body}>
                    <BreakForm title="Conversa" showAction={false} borderColor="var(--border-subtle-color)" />
                    <FormGroup labelText="Busca por contato">
                        <Input type="search" placeholder="Nome ou identificador (mínimo 3 caracteres)" value={filters.search || ""} onChange={event => handleChange("search", event.target.value)} />
                    </FormGroup>
                    <FormGroup labelText="Leitura">
                        <Select name="Leitura" placeholder="Todas" options={readStatusOptions} selected={filters.readStatus || []} onChange={value => handleChange("readStatus", value)} multiple={true} />
                    </FormGroup>
                    <FormGroup labelText="Situação">
                        <Select name="Situação" placeholder="Todas" options={statusOptions} selected={filters.status || []} onChange={value => handleChange("status", value)} multiple={true} />
                    </FormGroup>
                    <FormGroup labelText="Período">
                        <div className={styles.correlatedFilter}>
                            <div>
                                <span>Intervalo</span>
                                <DatePicker multiple={true} showTime={true} name="Período" dateStart={filters.dateStart || ""} dateEnd={filters.dateEnd || ""} timeStart={filters.timeStart || ""} timeEnd={filters.timeEnd || ""} onChange={handleChangePeriod} />
                            </div>
                            <div>
                                <span>Data considerada</span>
                                <Select name="Tipo de data" options={dateTypeOptions} selected={filters.typeDate || "last_message"} onChange={value => handleChange("typeDate", value)} multiple={false} />
                            </div>
                        </div>
                    </FormGroup>

                    <BreakForm title="Atendimento" showAction={false} borderColor="var(--border-subtle-color)" />
                    <FormGroup labelText="Caixa de entrada">
                        <Select name="Caixas de entrada" placeholder="Todas as caixas permitidas" options={inboxOptions} selected={filters.inboxes || []} onChange={value => handleChange("inboxes", value)} multiple={true} />
                    </FormGroup>
                    <FormGroup labelText="Etiquetas">
                        <Select name="Etiquetas" placeholder="Todas" options={flagOptions} selected={filters.flags || []} onChange={value => handleChange("flags", value)} multiple={true} />
                    </FormGroup>
                    <FormGroup labelText="Equipe responsável">
                        <Select name="Equipes" placeholder="Todas" options={teams || []} selected={filters.teams || []} onChange={value => handleChange("teams", value)} multiple={true} />
                    </FormGroup>
                    <FormGroup labelText="Cargo responsável">
                        <Select name="Cargos" placeholder="Todos" options={roles || []} selected={filters.roles || []} onChange={value => handleChange("roles", value)} multiple={true} />
                    </FormGroup>
                    <FormGroup labelText="Responsável">
                        <Select name="Responsáveis" placeholder="Todos" options={getUsersBySelectedTeams(filters.teams, users, teams)} selected={filters.users || []} onChange={value => handleChange("users", value)} multiple={true} />
                    </FormGroup>
                    <FormGroup labelText="Agente de IA">
                        <Select name="Agentes de IA" placeholder="Todos" options={aiAgents} selected={filters.aiAgents || []} onChange={value => handleChange("aiAgents", value)} multiple={true} />
                    </FormGroup>
                </div>

                <div className={styles.footer}>
                    <Button variant="secondary" onClick={() => setFilters({ typeDate: "last_message" })} disabled={loading}>Limpar</Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={loading}>Solicitar relatório</Button>
                </div>
            </div>
            {loading && <Loading fullPage={true} />}
        </div>
    )
}

export default ReportDataChat
